import { apiMerchantPublic } from "@/api";
import { apiMerchantURL } from "@/consts";

import type { SuggestionEvent } from "./types";

const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 20;
const EVENTS_TIMEOUT_MS = 1500;

/**
 * WHERE THE STOREFRONT RECORDS SUGGESTION EVENTS
 *
 * `POST /public/merchants/:id/suggestions/events`, fire-and-forget: the
 * gateway answers `202` unconditionally (fail-silent), so nothing here ever
 * blocks the UI or retries on failure. Queues in memory, flushes every ~5s
 * and eagerly when the tab is hidden/closed (`visibilitychange`/`pagehide`,
 * via `fetch(..., { keepalive: true })` so the request survives unload).
 */
export class SuggestionEventsSink {
  private readonly merchantId: string;
  private queue: SuggestionEvent[] = [];
  private readonly impressedKeys = new Set<string>();
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(merchantId: string) {
    this.merchantId = merchantId;
  }

  /** Session-stable key for "have we already recorded this impression?". */
  private impressionKey(surface: SuggestionEvent["surface"], itemIds: string[]): string {
    return `${surface}:${[...itemIds].sort().join(",")}`;
  }

  /**
   * Queues an event. An `IMPRESSION` is deduped per session per
   * surface+anchor-set — the strip's IntersectionObserver fires again on
   * every remount, and the backend counter must only see it once.
   */
  enqueue(event: SuggestionEvent): void {
    if (event.type === "IMPRESSION") {
      const key = this.impressionKey(event.surface, event.itemIds);
      if (this.impressedKeys.has(key)) return;
      this.impressedKeys.add(key);
    }

    this.queue.push(event);
    if (this.queue.length >= MAX_BATCH_SIZE) void this.flush();
  }

  /** Starts the periodic flush + unload listeners. Idempotent. */
  start(): () => void {
    if (this.intervalId === null) {
      this.intervalId = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
    }

    const onHide = () => {
      if (document.visibilityState === "hidden") this.flushOnUnload();
    };
    const onPageHide = () => this.flushOnUnload();

    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onHide);
    if (typeof window !== "undefined") window.addEventListener("pagehide", onPageHide);

    return () => {
      if (this.intervalId !== null) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onHide);
      if (typeof window !== "undefined") window.removeEventListener("pagehide", onPageHide);
    };
  }

  /** Sends up to `MAX_BATCH_SIZE` queued events. Never throws; drops the batch on failure. */
  async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const batch = this.queue.slice(0, MAX_BATCH_SIZE);
    this.queue = this.queue.slice(MAX_BATCH_SIZE);

    try {
      await apiMerchantPublic.post(
        `/public/merchants/${encodeURIComponent(this.merchantId)}/suggestions/events`,
        { events: batch },
        { timeout: EVENTS_TIMEOUT_MS },
      );
    } catch {
      return;
    }
  }

  /**
   * Best-effort send on tab hide/unload via `fetch(keepalive)` — axios does
   * not support `keepalive`, and an unload is not the moment to await a
   * promise anyway. Never rejects: a frozen/offline tab just drops the batch.
   */
  private flushOnUnload(): void {
    if (this.queue.length === 0) return;
    const batch = this.queue.slice(0, MAX_BATCH_SIZE);
    this.queue = this.queue.slice(MAX_BATCH_SIZE);

    try {
      const url = `${apiMerchantURL.replace(/\/merchants\/?$/, "")}/public/merchants/${encodeURIComponent(
        this.merchantId,
      )}/suggestions/events`;
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      return;
    }
  }

  /** Tests only. */
  reset(): void {
    this.queue = [];
    this.impressedKeys.clear();
  }
}

const sinksByMerchant = new Map<string, SuggestionEventsSink>();

/** One sink per merchant per session — the storefront never juggles two stores at once, but tests do. */
export function getSuggestionEventsSink(merchantId: string): SuggestionEventsSink {
  let sink = sinksByMerchant.get(merchantId);
  if (!sink) {
    sink = new SuggestionEventsSink(merchantId);
    sinksByMerchant.set(merchantId, sink);
  }
  return sink;
}

/** Tests only: forget every sink created so far. */
export function resetSuggestionEventsSinks(): void {
  sinksByMerchant.clear();
}
